import type {
  PeopleGrantMode,
  PeoplePerson,
  ResolvedPerson,
} from "@playbookai/types";
import type { PeopleFieldResolvedLabels } from "./people-field-labels.js";
import type { PeopleFieldValue } from "./people-field.types.js";

/* The chip's rule (d34, d39): which name it shows, which avatar, and which
   tag. Pure, so every branch of the tag rule is tested without a DOM. */

/** What `source.resolve` has said about the value's id so far. */
export type PeopleFieldResolveState =
  | { readonly status: "idle" }
  | { readonly status: "pending" }
  | { readonly status: "failed" }
  | { readonly status: "done"; readonly person: ResolvedPerson | null };

/** `pending` is a grant still to happen; `warning` is no access or inactive. */
export type PeopleFieldChipTone = "neutral" | "pending" | "warning";

export interface PeopleFieldChipView {
  readonly name: string;
  /** `seed` is the person's own colour; `unknown` an id resolve does not know. */
  readonly avatar: "seed" | "inactive" | "typed" | "unknown";
  readonly tag: {
    readonly label: string;
    readonly tone: PeopleFieldChipTone;
  } | null;
}

export interface PeopleFieldChipInput {
  readonly value: Exclude<PeopleFieldValue, null>;
  /** The value's entry in `source.roster()`, when it has one. */
  readonly rosterPerson: PeoplePerson | undefined;
  readonly grantMode: PeopleGrantMode;
  readonly pendingGrant: boolean;
  readonly resolved: PeopleFieldResolveState;
  readonly labels: PeopleFieldResolvedLabels;
}

export function peopleFieldChipView({
  value,
  rosterPerson,
  grantMode,
  pendingGrant,
  resolved,
  labels,
}: PeopleFieldChipInput): PeopleFieldChipView {
  if (value.kind === "typed") {
    return {
      name: value.name,
      avatar: "typed",
      tag: { label: labels.notLinked, tone: "neutral" },
    };
  }
  if (rosterPerson) {
    return {
      name: rosterPerson.name,
      avatar: "seed",
      tag: rosterPerson.access
        ? { label: rosterPerson.access.label, tone: "neutral" }
        : null,
    };
  }
  // An id resolve does not know (pasted from another workspace): name only.
  if (resolved.status === "done" && resolved.person === null) {
    return { name: value.name, avatar: "unknown", tag: null };
  }
  const person = resolved.status === "done" ? resolved.person : null;
  const inactive = person !== null && !person.active;
  const name = person?.name ?? value.name;
  const avatar = inactive ? "inactive" : "seed";
  const notActive = { label: labels.notActive, tone: "warning" as const };
  if (grantMode.kind === "none") {
    return { name, avatar, tag: inactive ? notActive : null };
  }
  if (pendingGrant && grantMode.kind === "grant") {
    return {
      name,
      avatar,
      tag: {
        label: labels.pendingGrant(grantMode.grantRoleLabel),
        tone: "pending",
      },
    };
  }
  if (inactive) return { name, avatar, tag: notActive };
  // Still asking: no tag yet, rather than a "No access" that may turn out to
  // be "Not active" a moment later.
  if (resolved.status === "pending") return { name, avatar, tag: null };
  return {
    name,
    avatar,
    tag: { label: labels.noAccess, tone: "warning" },
  };
}
